// src/services/sessionService.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage, auth } from './firebase';
import LearnerService from './learnerService';

const COLLECTIONS = {
  SESSIONS: 'taskdata-sessions',
  LEARNERS: 'taskdata-learners'  
};

export class SessionService {

  static getCurrentUserId() {
    return auth.currentUser?.uid;
  }

  static ensureAuthenticated() {
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  // File Upload Helpers
  static sanitizeFileName(fileName) {
    return fileName
      .replace(/[<>:"/\\|?*]/g, '-')
      .replace(/\s+/g, '_')
      .substring(0, 100);
  }

  // Upload lesson plan
  static async uploadLessonPlan(file, sessionId) {
    this.ensureAuthenticated();

    try {
      const sanitizedFileName = this.sanitizeFileName(file.name);
      const storageRef = ref(storage, `sessions/${sessionId}/lesson-plan/${sanitizedFileName}`);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploadedBy': this.ensureAuthenticated(),
          'uploadedAt': new Date().toISOString(),
          'originalName': file.name
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: snapshot.ref.fullPath,
        fileName: sanitizedFileName,
        originalName: file.name,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error('Error uploading lesson plan:', error);
      throw new Error('Failed to upload lesson plan: ' + error.message);
    }
  }

  // Upload resource file
  static async uploadResourceFile(file, sessionId, resourceId) {
    this.ensureAuthenticated();

    try {
      const sanitizedFileName = this.sanitizeFileName(file.name);
      const storageRef = ref(storage, `sessions/${sessionId}/resources/${resourceId}/${sanitizedFileName}`);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploadedBy': this.ensureAuthenticated(),
          'uploadedAt': new Date().toISOString(),
          'originalName': file.name,
          'resourceId': resourceId.toString()
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: snapshot.ref.fullPath,
        fileName: sanitizedFileName,
        originalName: file.name,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error('Error uploading resource file:', error);
      throw new Error('Failed to upload resource file: ' + error.message);
    }
  }

  // Delete file from storage
  static async deleteFile(filePath) {
    try {
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw new Error('Failed to delete file: ' + error.message);
    }
  }

  // Create session
  static async createSession(sessionData) {
    const userId = this.ensureAuthenticated();

    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.SESSIONS), {
        ...sessionData,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw new Error('Failed to create session: ' + error.message);
    }
  }

  // Update session
  static async updateSession(sessionId, updates) {
    const userId = this.ensureAuthenticated();

    try {
      const docRef = doc(db, COLLECTIONS.SESSIONS, sessionId);
      await updateDoc(docRef, {
        ...updates,
        updatedBy: userId,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating session:', error);
      throw new Error('Failed to update session: ' + error.message);
    }
  }

  // Get user sessions
  static async getUserSessions() {
    const userId = this.ensureAuthenticated();

    try {
      const q = query(
        collection(db, COLLECTIONS.SESSIONS),
        where('createdBy', '==', userId),
        orderBy('sessionDate', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sessionDate: doc.data().sessionDate?.toDate ? doc.data().sessionDate.toDate() : doc.data().sessionDate,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt
      }));
    } catch (error) {
      console.error('Error getting user sessions:', error);
      throw new Error('Failed to load sessions: ' + error.message);
    }
  }

  // Get single session
  static async getSession(sessionId) {
    const userId = this.ensureAuthenticated();

    try {
      const docRef = doc(db, COLLECTIONS.SESSIONS, sessionId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Session not found');
      }

      const data = docSnap.data();
      
      // Check if user has access to this session
      if (data.createdBy !== userId) {
        throw new Error('Permission denied');
      }

      return {
        id: docSnap.id,
        ...data,
        sessionDate: data.sessionDate?.toDate ? data.sessionDate.toDate() : data.sessionDate,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
      };
    } catch (error) {
      console.error('Error getting session:', error);
      throw new Error('Failed to load session: ' + error.message);
    }
  }

  // Delete session
  static async deleteSession(sessionId) {
    const userId = this.ensureAuthenticated();

    try {
      // Get session to check ownership
      const session = await this.getSession(sessionId);
      
      if (session.createdBy !== userId) {
        throw new Error('Permission denied');
      }

      // Delete associated files from storage
      if (session.lessonPlanPath) {
        await this.deleteFile(session.lessonPlanPath);
      }

      // Delete resource files
      if (session.resources) {
        for (const resource of session.resources) {
          if (resource.type === 'file' && resource.filePath) {
            await this.deleteFile(resource.filePath);
          }
        }
      }

      // Delete session document
      const docRef = doc(db, COLLECTIONS.SESSIONS, sessionId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting session:', error);
      throw new Error('Failed to delete session: ' + error.message);
    }
  }

  static async getSessionLearners(contextId, grades, subject) {
    try {
      const { LearnerService } = await import('./learnerService');
      
      // Get all learners for the specified grades and subject
      const allLearners = [];
      
      for (const grade of grades) {
        const gradeLearners = await LearnerService.getLearners(grade, subject);
        allLearners.push(...gradeLearners);
      }

      // Remove duplicates based on learner identity
      const uniqueLearners = allLearners.reduce((unique, learner) => {
        const key = `${learner.learnerSurname}-${learner.learnerName || learner.firstName}`;
        if (!unique.find(l => `${l.learnerSurname}-${l.learnerName || l.firstName}` === key)) {
          unique.push(learner);
        }
        return unique;
      }, []);

      // Sort by surname
      return uniqueLearners.sort((a, b) => 
        (a.learnerSurname || a.surname || '').localeCompare(b.learnerSurname || b.surname || '')
      );
    } catch (error) {
      console.error('Error fetching session learners:', error);
      throw new Error('Failed to fetch session learners');
    }
  }

  static async getSessionLearnersByContext(contextName, contextType, grades, subject) {
    const userId = this.ensureAuthenticated();

    try {
      console.log('Loading learners with context:', { contextName, contextType, grades, subject });
      
      const allLearners = [];
      
      for (const grade of grades) {
        if (!grade.trim()) continue;
        
        let gradeLearners = [];
        let queries = [];
        
        if (contextType === 'institution') {
          // PRIORITIZE enrolledSubjects array queries first
          queries = [
            // Primary: enrolledSubjects array queries (most common)
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('enrolledSubjects', 'array-contains', subject),
              where('schoolName', '==', contextName)
            ),
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('enrolledSubjects', 'array-contains', subject),
              where('school', '==', contextName)
            ),
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('enrolledSubjects', 'array-contains', subject),
              where('institution', '==', contextName)
            ),
            // Fallback: single subject field (for backward compatibility)
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('subject', '==', subject),
              where('schoolName', '==', contextName)
            ),
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('subject', '==', subject),
              where('school', '==', contextName)
            ),
            // Last resort: no subject filter, filter client-side
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('schoolName', '==', contextName)
            ),
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('school', '==', contextName)
            )
          ];
        } else if (contextType === 'project') {
          // Same logic for projects
          queries = [
            // Primary: enrolledSubjects array queries
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('enrolledSubjects', 'array-contains', subject),
              where('projectName', '==', contextName)
            ),
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('enrolledSubjects', 'array-contains', subject),
              where('project', '==', contextName)
            ),
            // Fallback: single subject field
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('subject', '==', subject),
              where('projectName', '==', contextName)
            ),
            // Last resort: no subject filter
            query(
              collection(db, COLLECTIONS.LEARNERS),
              where('createdBy', '==', userId),
              where('grade', '==', grade.trim()),
              where('projectName', '==', contextName)
            )
          ];
        }

        // TRY ALL QUERIES and collect results
        for (let i = 0; i < queries.length; i++) {
          try {
            console.log(`Executing query ${i + 1}/${queries.length} for grade: ${grade.trim()}`);
            const querySnapshot = await getDocs(queries[i]);
            
            if (!querySnapshot.empty) {
              const queryLearners = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  // Normalize the fields for consistency
                  learnerSurname: data.surname || data.learnerSurname,
                  learnerName: data.firstName || data.learnerName,
                  school: data.schoolName || data.school,
                  project: data.projectName || data.project
                };
              });
              
              console.log(`Query ${i + 1} returned ${queryLearners.length} learners`);
              gradeLearners.push(...queryLearners);
            } else {
              console.log(`Query ${i + 1} returned 0 learners`);
            }
          } catch (queryError) {
            console.log(`Query ${i + 1} failed:`, queryError.message);
          }
        }

        // Client-side filtering for subject if we got learners without subject filter
        if (gradeLearners.length > 0) {
          const filteredLearners = gradeLearners.filter(learner => {
            // If learner has enrolledSubjects array, check if subject is included
            if (learner.enrolledSubjects && Array.isArray(learner.enrolledSubjects)) {
              return learner.enrolledSubjects.includes(subject);
            }
            // If learner has single subject field, check exact match
            if (learner.subject) {
              return learner.subject === subject;
            }
            // If no subject data, exclude for safety
            return false;
          });
          
          console.log(`Found ${filteredLearners.length} learners for grade ${grade} after subject filtering`);
          allLearners.push(...filteredLearners);
        }
      }

      // Remove duplicates based on learner identity
      const uniqueLearners = allLearners.reduce((unique, learner) => {
        const key = `${learner.learnerSurname}-${learner.learnerName}-${learner.grade}`;
        if (!unique.find(l => `${l.learnerSurname}-${l.learnerName}-${l.grade}` === key)) {
          unique.push(learner);
        }
        return unique;
      }, []);

      console.log(`Total unique learners found: ${uniqueLearners.length}`);

      // Sort by surname, then by grade
      return uniqueLearners.sort((a, b) => {
        const surnameCompare = (a.learnerSurname || '').localeCompare(b.learnerSurname || '');
        if (surnameCompare !== 0) return surnameCompare;
        return (a.grade || '').localeCompare(b.grade || '');
      });

    } catch (error) {
      console.error('Error fetching session learners by context:', error);
      throw new Error('Failed to fetch session learners: ' + error.message);
    }
  }
}